import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { userUploadsAPI } from '../api/client'
import { Check, X, Clock, Eye, Trash2, Upload, User, Globe, Lock } from 'lucide-react'

function UploadCard({ upload, onApprove, onReject }) {
  const isApproved = upload.is_approved === 1
  const isPublishRequested = upload.publish_requested
  const isDeleted = upload.is_deleted

  return (
    <div className="glass p-3 group">
      {/* Thumbnail */}
      <div className="aspect-square rounded-lg overflow-hidden bg-white/5 mb-3 relative">
        <img
          src={upload.preview_url || upload.image_url}
          alt={upload.title || 'Upload'}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => { e.target.src = upload.image_url }}
        />

        {/* Hover overlay with actions */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
          {upload.image_url && (
            <a
              href={upload.image_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 bg-white/20 rounded-full hover:bg-white/40"
              title="Открыть оригинал"
            >
              <Eye size={18} />
            </a>
          )}
          {!isApproved && !isDeleted && (
            <button
              onClick={() => onApprove(upload.id)}
              className="p-2 bg-green-500/50 rounded-full hover:bg-green-500"
              title="Одобрить"
            >
              <Check size={18} />
            </button>
          )}
          <button
            onClick={() => onReject(upload.id)}
            className="p-2 bg-red-500/50 rounded-full hover:bg-red-500"
            title="Отклонить"
          >
            <X size={18} />
          </button>
        </div>

        {/* Status badge */}
        {isApproved ? (
          <div className="absolute top-2 right-2 px-2 py-1 bg-green-500/80 rounded text-xs font-bold">
            Одобрено
          </div>
        ) : isDeleted ? (
          <div className="absolute top-2 right-2 px-2 py-1 bg-red-500/80 rounded text-xs font-bold">
            Отклонено
          </div>
        ) : isPublishRequested ? (
          <div className="absolute top-2 right-2 px-2 py-1 bg-amber-500/80 rounded text-xs font-bold flex items-center gap-1">
            <Globe size={10} />
            На публикацию
          </div>
        ) : (
          <div className="absolute top-2 right-2 px-2 py-1 bg-gray-500/80 rounded text-xs font-bold flex items-center gap-1">
            <Lock size={10} />
            Приватная
          </div>
        )}
      </div>

      {/* Info */}
      <div className="text-xs space-y-1">
        {upload.title && (
          <div className="font-medium truncate">{upload.title}</div>
        )}
        {upload.category_name && (
          <div className="opacity-60 truncate">{upload.category_name}</div>
        )}
        <div className="flex items-center gap-1 opacity-50">
          <User size={10} />
          <span className="truncate">{upload.source_name || `User #${upload.tg_user_id}`}</span>
        </div>
        {upload.created_at && (
          <div className="opacity-40">
            {new Date(upload.created_at).toLocaleDateString('ru-RU')}
          </div>
        )}
      </div>
    </div>
  )
}

export default function UserUploads() {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('public')

  // Stats
  const { data: stats } = useQuery(
    'uploadStats',
    () => userUploadsAPI.getStats(),
    { refetchInterval: 30000 }
  )

  // Uploads list
  const { data, isLoading } = useQuery(
    ['userUploads', activeTab],
    () => userUploadsAPI.getPending({ status: activeTab, limit: 100 }),
    { keepPreviousData: true }
  )

  // Approve mutation
  const approveMutation = useMutation(
    (id) => userUploadsAPI.approve(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('userUploads')
        queryClient.invalidateQueries('uploadStats')
      }
    }
  )

  // Reject mutation
  const rejectMutation = useMutation(
    (id) => userUploadsAPI.reject(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('userUploads')
        queryClient.invalidateQueries('uploadStats')
      }
    }
  )

  const handleApprove = (id) => {
    approveMutation.mutate(id)
  }

  const handleReject = (id) => {
    if (confirm('Отклонить загрузку?')) {
      rejectMutation.mutate(id)
    }
  }

  const tabs = [
    { key: 'public', label: 'На публикацию', count: stats?.public_pending, color: 'bg-amber-600' },
    { key: 'private', label: 'Приватные', count: stats?.private_pending, color: 'bg-gray-600' },
    { key: 'approved', label: 'Одобренные', count: stats?.approved, color: 'bg-green-600' },
    { key: 'rejected', label: 'Отклонённые', count: stats?.rejected, color: 'bg-red-600' },
    { key: 'all', label: 'Все', count: stats?.total, color: 'bg-white/20' },
  ]

  const emptyMessages = {
    public: 'Нет загрузок на публикацию',
    private: 'Нет приватных загрузок',
    approved: 'Нет одобренных загрузок',
    rejected: 'Нет отклонённых загрузок',
    all: 'Нет загрузок',
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Upload size={28} className="text-purple-400" />
          <div>
            <h1 className="text-2xl font-bold">Загрузки пользователей</h1>
            <p className="text-sm opacity-50">Модерация открыток из «Мой канал»</p>
          </div>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-5 gap-3 mb-6">
        <div className="glass p-4 text-center">
          <div className="text-2xl font-bold text-amber-400">{stats?.public_pending || 0}</div>
          <div className="text-xs opacity-50">На публикацию</div>
        </div>
        <div className="glass p-4 text-center">
          <div className="text-2xl font-bold text-gray-400">{stats?.private_pending || 0}</div>
          <div className="text-xs opacity-50">Приватные</div>
        </div>
        <div className="glass p-4 text-center">
          <div className="text-2xl font-bold text-green-400">{stats?.approved || 0}</div>
          <div className="text-xs opacity-50">Одобрено</div>
        </div>
        <div className="glass p-4 text-center">
          <div className="text-2xl font-bold text-red-400">{stats?.rejected || 0}</div>
          <div className="text-xs opacity-50">Отклонено</div>
        </div>
        <div className="glass p-4 text-center">
          <div className="text-2xl font-bold">{stats?.total || 0}</div>
          <div className="text-xs opacity-50">Всего</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {tabs.map(({ key, label, count, color }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`px-4 py-2 rounded-lg transition-all ${
              activeTab === key ? `${color} text-white` : 'bg-white/10 hover:bg-white/20'
            }`}
          >
            {label}
            {count > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-xs">{count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="text-center py-12 opacity-50">Загрузка...</div>
      ) : !data?.uploads?.length ? (
        <div className="text-center py-12 opacity-50">
          {emptyMessages[activeTab] || 'Нет загрузок'}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {data.uploads.map(upload => (
            <UploadCard
              key={upload.id}
              upload={upload}
              onApprove={handleApprove}
              onReject={handleReject}
            />
          ))}
        </div>
      )}
    </div>
  )
}
